// Email service using EmailJS for sending all form submissions
// Sign up at https://www.emailjs.com/ to get your credentials

const EMAILJS_SERVICE_ID = 'service_pridepath'; // Replace with your EmailJS service ID
const EMAILJS_TEMPLATE_ID = 'template_contact'; // Replace with your EmailJS template ID  
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY'; // Replace with your EmailJS public key

// Recipient email address for all form submissions
const RECIPIENT_EMAIL = 'info@pridepath.lk';

// Initialize EmailJS (call this once in your app)
export const initEmailJS = () => {
    if (window.emailjs) {
        window.emailjs.init(EMAILJS_PUBLIC_KEY);
    }
};

// Format file size for display
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Send email via mailto (fallback method)
const sendViaMailto = (subject, body) => {
    const mailtoLink = `mailto:${RECIPIENT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const link = document.createElement('a');
    link.href = mailtoLink;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { success: true, method: 'mailto' };
};

// Store message locally and dispatch event for admin dashboard
const storeMessage = (type, data) => {
    const messages = JSON.parse(localStorage.getItem('siteMessages') || '[]');
    const newMessage = {
        id: Date.now().toString(),
        type: type,
        ...data,
        read: false,
        created_at: new Date().toISOString()
    };
    messages.unshift(newMessage);
    localStorage.setItem('siteMessages', JSON.stringify(messages));

    // Dispatch event for real-time updates
    window.dispatchEvent(new CustomEvent('newMessage', { detail: newMessage }));

    return newMessage;
};

// ==================== CONTACT FORM ====================
export const sendContactMessage = async (formData) => {
    const { name, email, phone, subject, message } = formData;

    const emailBody = `
CONTACT FORM MESSAGE
====================

SENDER INFORMATION
------------------
Name: ${name}
Email: ${email}
Phone: ${phone || 'Not provided'}

MESSAGE
-------
Subject: ${subject || 'General Inquiry'}
${message}

---
Submitted: ${new Date().toLocaleString()}
Source: Website Contact Form
    `.trim();

    // Store message locally for admin dashboard
    storeMessage('contact', {
        name,
        email,
        phone,
        subject: subject || 'General Inquiry',
        message
    });

    // Try EmailJS first
    if (window.emailjs && EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
        try {
            await window.emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    to_email: RECIPIENT_EMAIL,
                    from_name: name,
                    from_email: email,
                    subject: subject || 'Contact Form Message',
                    message: emailBody,
                    reply_to: email
                }
            );
            console.log('✅ Contact message sent via EmailJS to', RECIPIENT_EMAIL);
            return { success: true, method: 'emailjs' };
        } catch (error) {
            console.warn('EmailJS failed, using mailto fallback:', error);
        }
    }

    // Fallback to mailto
    return sendViaMailto(subject || 'Contact Form Message', emailBody);
};

// ==================== LOAN APPLICATION ====================
export const sendLoanApplication = async (applicationData, files = []) => {
    const fileInfo = files.map(f => ({
        name: f.name,
        size: formatFileSize(f.size),
        type: f.type
    }));

    const emailBody = `
LOAN APPLICATION RECEIVED
=========================

LOAN TYPE: ${applicationData.loanType || applicationData.productTitle || 'General Loan'}
SUBMITTED: ${new Date().toLocaleString()}

PERSONAL INFORMATION
-------------------
Full Name: ${applicationData.fullName}
NIC Number: ${applicationData.nic || 'Not provided'}
Phone: ${applicationData.phone}
Email: ${applicationData.email}
Address: ${applicationData.address || 'Not provided'}

${applicationData.employerName || applicationData.employmentType ? `EMPLOYMENT DETAILS
------------------
Employment Type: ${applicationData.employmentType || 'N/A'}
Employer: ${applicationData.employerName || 'N/A'}
Monthly Income: LKR ${applicationData.monthlyIncome?.toLocaleString() || 'N/A'}
Years Employed: ${applicationData.yearsEmployed || 'N/A'}
` : ''}

${applicationData.businessName ? `BUSINESS DETAILS
----------------
Business Name: ${applicationData.businessName}
Registration No: ${applicationData.businessRegNo || 'N/A'}
Years in Business: ${applicationData.yearsInBusiness || 'N/A'}
` : ''}

LOAN DETAILS
------------
Amount Requested: LKR ${(applicationData.amountRequested || applicationData.loanAmount)?.toLocaleString() || 'Not specified'}
Preferred Tenure: ${applicationData.tenure} months
Purpose: ${applicationData.purpose || 'N/A'}

${fileInfo.length > 0 ? `UPLOADED DOCUMENTS
------------------
${fileInfo.map(f => `- ${f.name} (${f.size})`).join('\n')}` : ''}

Additional Notes: ${applicationData.notes || 'None'}

---
Source: Website Loan Application
    `.trim();

    // Store as application for admin dashboard
    storeMessage('application', {
        name: applicationData.fullName,
        email: applicationData.email,
        phone: applicationData.phone,
        productTitle: applicationData.loanType || applicationData.productTitle,
        message: `Loan Amount: LKR ${(applicationData.amountRequested || applicationData.loanAmount)?.toLocaleString() || 'N/A'} | Tenure: ${applicationData.tenure} months`,
        applicationData: applicationData
    });

    // Try EmailJS first
    if (window.emailjs && EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
        try {
            await window.emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    to_email: RECIPIENT_EMAIL,
                    from_name: applicationData.fullName,
                    from_email: applicationData.email,
                    subject: `Loan Application - ${applicationData.loanType || applicationData.productTitle || 'New Application'}`,
                    message: emailBody,
                    reply_to: applicationData.email
                }
            );
            console.log('✅ Loan application sent via EmailJS to', RECIPIENT_EMAIL);
            return { success: true, method: 'emailjs' };
        } catch (error) {
            console.warn('EmailJS failed, using mailto fallback:', error);
        }
    }

    // Fallback to mailto
    return sendViaMailto(
        `Loan Application - ${applicationData.loanType || applicationData.productTitle || 'New Application'} - ${applicationData.fullName}`,
        emailBody
    );
};

// ==================== JOB APPLICATION ====================
export const sendJobApplication = async (applicationData, resumeFile = null) => {
    const emailBody = `
JOB APPLICATION RECEIVED
========================

POSITION: ${applicationData.position || 'General Application'}
SUBMITTED: ${new Date().toLocaleString()}

APPLICANT INFORMATION
---------------------
Full Name: ${applicationData.fullName || applicationData.name}
Email: ${applicationData.email}
Phone: ${applicationData.phone}

QUALIFICATIONS
--------------
Education: ${applicationData.education || 'Not specified'}
Experience: ${applicationData.experience || 'Not specified'}
${resumeFile ? `Resume: ${resumeFile.name} (${formatFileSize(resumeFile.size)})` : 'Resume: Not attached'}

COVER LETTER
------------
${applicationData.coverLetter || applicationData.message || 'Not provided'}

---
Source: Website Careers Page
    `.trim();

    // Store for admin dashboard
    storeMessage('application', {
        name: applicationData.fullName || applicationData.name,
        email: applicationData.email,
        phone: applicationData.phone,
        productTitle: `Job Application: ${applicationData.position || 'General'}`,
        message: applicationData.coverLetter || applicationData.message || 'No cover letter provided'
    });

    // Try EmailJS first
    if (window.emailjs && EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
        try {
            await window.emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    to_email: RECIPIENT_EMAIL,
                    from_name: applicationData.fullName || applicationData.name,
                    from_email: applicationData.email,
                    subject: `Job Application - ${applicationData.position || 'Career Inquiry'}`,
                    message: emailBody,
                    reply_to: applicationData.email
                }
            );
            console.log('✅ Job application sent via EmailJS to', RECIPIENT_EMAIL);
            return { success: true, method: 'emailjs' };
        } catch (error) {
            console.warn('EmailJS failed, using mailto fallback:', error);
        }
    }

    // Fallback to mailto
    return sendViaMailto(
        `Job Application - ${applicationData.position || 'Career Inquiry'} - ${applicationData.fullName || applicationData.name}`,
        emailBody
    );
};

// ==================== GENERAL EMAIL SENDER ====================
export const sendEmail = async (type, formData, files = []) => {
    switch (type) {
        case 'contact':
            return sendContactMessage(formData);
        case 'loan':
        case 'application':
        case 'product':
            return sendLoanApplication(formData, files);
        case 'job':
        case 'career':
            return sendJobApplication(formData, files[0]);
        default:
            return sendContactMessage(formData);
    }
};

// Get recipient email (for display purposes)
export const getRecipientEmail = () => RECIPIENT_EMAIL;

// Get all stored applications (for admin view)
export const getStoredApplications = () => {
    return JSON.parse(localStorage.getItem('loanApplications') || '[]');
};

// Clear stored applications
export const clearStoredApplications = () => {
    localStorage.removeItem('loanApplications');
};

export default {
    initEmailJS,
    sendContactMessage,
    sendLoanApplication,
    sendJobApplication,
    sendEmail,
    getRecipientEmail,
    RECIPIENT_EMAIL
};
